/**
 * Pure helpers behind the public services page (`/servizi`).
 *
 * The page shows one card per WP `servizio` -> `acf.codes[]` row, so a single
 * service fans out into all of its bookable codes. Nothing here talks to the
 * Axalta/Tekio booking API: everything comes from the WordPress payload the
 * root loader already puts in `servicesData` / `serviceCategoriesData`.
 */

export interface ServiceCatalogCategory {
  id: number;
  name: string;
  slug: string;
}

export interface ServiceCatalogItem {
  /** Stable react key: a code can legitimately repeat across services. */
  key: string;
  code: string;
  title: string;
  /**
   * Rich text, may be empty. Rendered with dangerouslySetInnerHTML.
   *
   * This is the catalogue's own copy (`catalog_description`), not the booking
   * flow's `long_description` - see `buildServiceCatalog` for why it can still
   * end up holding the latter.
   */
  description: string;
  imageId?: number;
  serviceId: number;
  serviceTitle: string;
  categoryIds: number[];
}

export interface ServiceCatalog {
  items: ServiceCatalogItem[];
  categories: ServiceCatalogCategory[];
}

/**
 * ACF `true_false` read over REST. Anything that is not an explicit truthy
 * value counts as hidden: a code whose flag never made it into the payload
 * must not leak onto a public page.
 */
export function isVisibleOnFront(value: unknown): boolean {
  return value === true || value === 1 || value === "1" || value === "true";
}

function imageId(image: any): number | undefined {
  if (!image) return undefined;
  if (typeof image === "number") return image;
  const id = image.id ?? image.ID;
  return typeof id === "number" ? id : undefined;
}

/**
 * Numeric ordering hint on a WP term, if one is actually exposed over REST.
 *
 * The "Category Order and Taxonomy Terms Order" plugin keeps the manual order
 * in the `term_order` column of `wp_term_taxonomy` and applies it by filtering
 * `get_terms`. That column is NOT part of the REST term schema: as of this
 * change `/wp-json/wp/v2/service_category` returns no `term_order`, `meta` is
 * `[]`, and `?orderby=term_order` is rejected with `rest_invalid_param`
 * (`orderby is not one of id, include, name, slug, include_slugs, term_group,
 * description, and count`). So the plugin's order survives only as the ORDER
 * OF THE RESPONSE ARRAY.
 *
 * This reader exists for the day WP does expose the value (a `register_meta`
 * / `register_rest_field` on the WP side, or a plugin update): if it appears,
 * it wins over the array order and no front-end change is needed. Until then
 * it returns null for every term and the array order is used as-is.
 */
function termOrderValue(term: any): number | null {
  const candidates = [
    term?.term_order,
    term?.menu_order,
    term?.meta?.term_order,
    term?.acf?.term_order,
  ];

  for (const candidate of candidates) {
    if (candidate === null || candidate === undefined || candidate === "") continue;
    const value = Number(candidate);
    if (Number.isFinite(value)) return value;
  }

  return null;
}

/**
 * Orders the category tabs the way WordPress asked for.
 *
 * Two tiers, in this order:
 *  1. an explicit numeric order field, but ONLY if every term carries one -
 *     a partial field would silently mix two orderings;
 *  2. otherwise the payload order untouched, which is what the WP plugin
 *     produced (`Array.prototype.sort` is stable, so a comparator that always
 *     returns 0 is a no-op on the input order).
 *
 * What it deliberately does NOT do is re-sort by id or by name. Term id order
 * is creation order and has nothing to do with the manual order: on production
 * the plugin returns ids 155, 153, 157, 159 (Ingresso first), so the previous
 * `.sort((a, b) => a.id - b.id)` moved `Food & Beverage` to the front and threw
 * away exactly the order the plugin had just applied. Alphabetical (the REST
 * default when no plugin reorders `get_terms`) is wrong for the same reason.
 *
 * The fallback is deterministic either way: with the plugin off, WP's own
 * default ordering is what comes through, identical between the worker
 * rendering the HTML and the browser hydrating it.
 */
export function sortCatalogTerms(terms: any[]): any[] {
  const ordered = terms.map((term, index) => ({
    term,
    index,
    order: termOrderValue(term),
  }));

  const everyTermHasOrder = ordered.every((entry) => entry.order !== null);
  if (!everyTermHasOrder) return ordered.map((entry) => entry.term);

  return ordered
    .sort((a, b) => (a.order! - b.order!) || (a.index - b.index))
    .map((entry) => entry.term);
}

/**
 * Manual order of a `servizio` post, as written by the "Post Types Order"
 * plugin.
 *
 * Unlike the taxonomy terms above, this one DOES survive REST: `menu_order` is
 * part of the WP post schema, and a live check on production
 * (`/wp-json/wp/v2/servizio?_fields=id,menu_order`) returns real values
 * (4, 8, 9, 10, 11, 12, 13) rather than the 0 a site without the plugin shows.
 * What does NOT survive is the ORDER OF THE ARRAY: the same request returns the
 * posts in WP's REST default (date, descending) — `orderby=date&order=asc`
 * comes back reversed, which proves the default is a real date sort and not the
 * plugin's. So for services, unlike for categories, the number is the signal
 * and the array order is not.
 *
 * The field reaches us because the root loader asks for it explicitly in the
 * `/wp/v2/servizio` call it already makes to attach `service_category` (the ACF
 * collection that carries everything else returns only `{ id, acf }`).
 */
function serviceOrderValue(service: any): number | null {
  const candidates = [service?.menu_order, service?.acf?.menu_order];

  for (const candidate of candidates) {
    if (candidate === null || candidate === undefined || candidate === "") continue;
    const value = Number(candidate);
    if (Number.isFinite(value)) return value;
  }

  return null;
}

/**
 * Builds the flat list of visible codes plus the categories that actually
 * hold at least one of them.
 *
 * Only services with a category attached are considered — that is the rule the
 * page is specified against, and it is also what makes the filter meaningful.
 *
 * Note: we deliberately use the FULL category list, not the booking flow's
 * `normalServiceCategories`. On this site `Food & Beverage` and `Trattamento`
 * carry `is_an_upselling`, and `Pacchetti` carries `hidden_on_booking`; those
 * flags describe the booking wizard, not this catalogue, and filtering on them
 * would leave a single tab.
 */
export function buildServiceCatalog(
  servicesData: any[] | undefined | null,
  serviceCategoriesData: any[] | undefined | null
): ServiceCatalog {
  const services = Array.isArray(servicesData) ? servicesData : [];
  const terms = Array.isArray(serviceCategoriesData) ? serviceCategoriesData : [];

  const knownCategoryIds = new Set<number>(
    terms.map((term: any) => Number(term.id)).filter((id: number) => Number.isFinite(id))
  );
  const items: ServiceCatalogItem[] = [];

  // Per-item provenance, kept out of `ServiceCatalogItem` because it is an
  // ordering detail nothing outside this function needs.
  const serviceIndexByItem = new Map<ServiceCatalogItem, number>();
  const serviceOrderByItem = new Map<ServiceCatalogItem, number | null>();

  services.forEach((service: any, serviceIndex: number) => {
    const acf = service?.acf ?? {};

    // `service_category` is merged in by the root loader from a second WP call;
    // treat anything that is not an array as "no category".
    const rawCategoryIds = Array.isArray(service?.service_category)
      ? service.service_category
      : [];

    const categoryIds: number[] = rawCategoryIds
      .map((id: any) => Number(id))
      .filter((id: number) => knownCategoryIds.has(id));

    if (categoryIds.length === 0) return;

    const codes = Array.isArray(acf.codes) ? acf.codes : [];
    const serviceOrder = serviceOrderValue(service);

    codes.forEach((entry: any, index: number) => {
      const code = String(entry?.code ?? "").trim();
      if (!code || !isVisibleOnFront(entry?.visible_on_front)) return;

      const item: ServiceCatalogItem = {
        key: `${service.id}-${index}-${code}`,
        code,
        // No post-title fallback on purpose: the ACF-to-REST collection this
        // comes from (`acf/v3/servizio`) returns only `{ id, acf }`, so neither
        // `title.rendered` nor `name` exists on it. `acf.title` is the title.
        title: String(entry?.short_description || acf?.title || code),
        // `catalog_description` is this page's own copy; `long_description`
        // belongs to the booking flow, which keeps reading it directly from
        // `servicesData` and is unaffected by this order. The fallback is not
        // temporary scaffolding: the field ships empty on every code, so
        // without it the popup would go blank on a live site, and it stays
        // useful for codes nobody ever writes a catalogue text for.
        description: String(
          entry?.catalog_description ||
            entry?.long_description ||
            acf?.description ||
            ""
        ),
        imageId: imageId(entry?.image) ?? imageId(acf?.image),
        serviceId: Number(service.id),
        serviceTitle: String(acf?.title || ""),
        categoryIds,
      };

      items.push(item);
      serviceIndexByItem.set(item, serviceIndex);
      serviceOrderByItem.set(item, serviceOrder);
    });
  });

  const usedCategoryIds = new Set<number>(items.flatMap((item) => item.categoryIds));

  // Category order follows WordPress, never our own idea of it: the manual
  // order set in WP admin ("Category Order and Taxonomy Terms Order") is
  // applied by that plugin to `get_terms`, so it reaches us as the ORDER OF
  // THE REST ARRAY - see `sortCatalogTerms` for why nothing else can be used.
  const categories: ServiceCatalogCategory[] = sortCatalogTerms(
    terms.filter((term: any) => usedCategoryIds.has(Number(term.id)))
  ).map((term: any) => ({
    id: Number(term.id),
    name: decodeEntities(String(term.name ?? "")),
    slug: String(term.slug ?? ""),
  }));

  // The unfiltered "Tutti" list reads this array straight through, so it has
  // to be grouped by category too - it used to be sorted A-Z by title, which
  // scattered the categories across the whole grid (Aperitivo, then two
  // massages, then a package...). Both layouts end up consistent this way:
  // the mobile carousels already slice by category via
  // `groupCatalogByCategory`, and "Tutti" now shows the same blocks in the
  // same order.
  //
  // Rank = position of the item's FIRST category in the ordered list above, so
  // a code attached to several categories lands under the earliest one, which
  // is where the reader looks for it. Nothing here depends on a runtime
  // locale, so the worker's HTML and the browser's hydration cannot disagree.
  const categoryRank = new Map<number, number>(
    categories.map((category, index) => [category.id, index])
  );
  const itemRank = new Map<ServiceCatalogItem, number>(
    items.map((item) => [
      item,
      item.categoryIds.reduce(
        (best, id) => Math.min(best, categoryRank.get(id) ?? Number.MAX_SAFE_INTEGER),
        Number.MAX_SAFE_INTEGER
      ),
    ])
  );

  // Inside a category, services follow the manual order of the `services` post
  // type (`menu_order`, written by "Post Types Order"), and inside a service
  // its codes keep the order of the ACF repeater. The payload order cannot be
  // used here the way it is for categories: `/wp/v2/servizio` comes back in
  // REST's default date order, so without this the newest service led its
  // category.
  //
  // All-or-nothing, like `sortCatalogTerms`: the numbers are used only if
  // EVERY service that produced a card carries one. A half-filled field would
  // silently mix two orderings, and a KV entry cached before the loader
  // started asking for `menu_order` has none at all - that stale window falls
  // back to the previous behaviour (payload order) instead of a scrambled
  // list, and heals itself when the 30 min cache expires.
  const everyServiceHasOrder = items.every(
    (item) => serviceOrderByItem.get(item) !== null
  );
  const serviceRank = (item: ServiceCatalogItem): number =>
    (everyServiceHasOrder
      ? serviceOrderByItem.get(item)
      : serviceIndexByItem.get(item)) ?? 0;

  // `sort` is stable, so items of the same service keep the `codes` order they
  // were pushed in and no third comparison term is needed.
  items.sort(
    (a, b) =>
      itemRank.get(a)! - itemRank.get(b)! || serviceRank(a) - serviceRank(b)
  );

  return { items, categories };
}

/** WP returns term names HTML-escaped (`Food &amp; Beverage`). */
export function decodeEntities(value: string): string {
  return value
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#0?39;/g, "'")
    .replace(/&#8217;/g, "’")
    .replace(/&nbsp;/g, " ");
}

/** Strips tags so a rich-text description can be matched by the search box. */
function plainText(value: string): string {
  return value.replace(/<[^>]*>/g, " ");
}

export function filterCatalogItems(
  items: ServiceCatalogItem[],
  categoryId: number | null,
  query: string
): ServiceCatalogItem[] {
  const needle = query.trim().toLowerCase();

  return items.filter((item) => {
    if (categoryId !== null && !item.categoryIds.includes(categoryId)) return false;
    if (!needle) return true;

    return (
      item.title.toLowerCase().includes(needle) ||
      item.serviceTitle.toLowerCase().includes(needle) ||
      plainText(item.description).toLowerCase().includes(needle)
    );
  });
}

export interface ServiceCatalogGroup {
  category: ServiceCatalogCategory;
  items: ServiceCatalogItem[];
}

/** Mobile layout: one carousel per category, in category order. */
export function groupCatalogByCategory(
  items: ServiceCatalogItem[],
  categories: ServiceCatalogCategory[]
): ServiceCatalogGroup[] {
  return categories
    .map((category) => ({
      category,
      items: items.filter((item) => item.categoryIds.includes(category.id)),
    }))
    .filter((group) => group.items.length > 0);
}

/** Resolves a `?categoria=` slug against the available categories. */
export function categoryIdFromSlug(
  slug: string | null | undefined,
  categories: ServiceCatalogCategory[]
): number | null {
  if (!slug) return null;
  return categories.find((category) => category.slug === slug)?.id ?? null;
}

export function categorySlugFromId(
  id: number | null,
  categories: ServiceCatalogCategory[]
): string | null {
  if (id === null) return null;
  return categories.find((category) => category.id === id)?.slug ?? null;
}

